/** Tab 4: Attendance */
import { useState } from 'react'
import {
  Button, Card, Form, Input, message, Space, DatePicker, Select, Switch, InputNumber, Row, Col,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { joiningApi, type JoiningDetail } from '@/features/hr/joiningApi'
import { shiftsApi } from '@/features/masters/shifts/api'
import { SHIFT_TYPE_POLICIES } from '@/shared/types/hr'

interface Props { detail: JoiningDetail }

const DAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']

// Owner policy (Sprint 0151): employees who JOIN on/after this date get their
// biometric number 100% system-generated. HR no longer types it for them — the
// field is shown read-only. Pre-cutover joiners keep the editable field.
const BIOMETRIC_AUTOGEN_CUTOVER = '2026-07-15'

export default function AttendanceTab({ detail }: Props) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [dirty, setDirty] = useState(false)

  // Shift master — for the default_shift_id picker. Same source as
  // ShiftRosterPage and SanctionedStrengthPage, including inactive
  // shifts so HR can pre-assign workers to shifts being planned.
  const { data: shifts = [] } = useQuery({
    queryKey: ['shifts-all'],
    queryFn: () => shiftsApi.list({}),
  })

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => joiningApi.update(detail.id, values),
    onSuccess: () => {
      message.success('Saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const initial: Record<string, unknown> = { ...detail.employee }
  if (initial.biometric_enrollment_date) {
    initial.biometric_enrollment_date = dayjs(initial.biometric_enrollment_date as string)
  }

  // Post-cutover joiners have a 100% system-generated biometric number — hide
  // the manual input and show a read-only display instead. Pre-cutover joiners
  // (and rows with no joining_date) keep the editable field untouched.
  const joiningDate = detail.employee.joining_date as string | null | undefined
  const isPostCutover = !!joiningDate
    && dayjs(joiningDate).isValid()
    && !dayjs(joiningDate).isBefore(dayjs(BIOMETRIC_AUTOGEN_CUTOVER), 'day')
  const assignedBiometricId = detail.employee.biometric_id as string | null | undefined

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initial}
      onValuesChange={() => setDirty(true)}
      onFinish={(values) => {
        const out: Record<string, unknown> = {}
        Object.keys(values).forEach((k) => {
          const v = values[k]
          out[k] = dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v
        })
        save.mutate(out)
      }}
    >
      <Card size="small" title="Attendance / Biometric" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name="attendance_applicable" label="Attendance Applicable" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="biometric_required" label="Biometric Required" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            {isPostCutover ? (
              /* Post-cutover joiner: biometric number is 100% system-generated
                 (owner policy). Show it read-only — HR cannot type/override. */
              <Form.Item
                label="Biometric ID (device no.)"
                tooltip="Assigned automatically by the system when CHRO gives final approval. HR cannot edit this for employees joining on/after 15 Jul 2026."
              >
                {assignedBiometricId ? (
                  <Input value={assignedBiometricId} disabled />
                ) : (
                  <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                    Assigned automatically once CHRO approves
                  </span>
                )}
              </Form.Item>
            ) : (
              /* Pre-cutover joiner: Biometric ID = the enrollment number
                 programmed on the physical eSSL/ZKTeco device. HR types the
                 SAME number here so the ERP matches the machine and punches map
                 to the right person. Uniqueness is enforced on save (friendly
                 error) and by the DB partial-unique index. Leave blank for
                 staff who don't punch (turn Attendance Applicable OFF). */
              <Form.Item
                name="biometric_id"
                label="Biometric ID (device no.)"
                tooltip="Enter the exact enrollment number from your biometric attendance machine for this employee. Must be unique. Leave blank for staff who don't punch (turn off Attendance Applicable)."
                rules={[
                  {
                    pattern: /^[0-9]+$/,
                    message: 'Numbers only — must match the device enrollment number',
                  },
                ]}
              >
                <Input
                  placeholder="e.g. 40245 (from the device)"
                  inputMode="numeric"
                />
              </Form.Item>
            )}
          </Col>
          <Col span={6}>
            <Form.Item name="biometric_enrolled" label="Biometric Enrolled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="biometric_enrollment_date" label="Enrollment Date">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="device_group" label="Device Group">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="weekly_off_day" label="Weekly Off Day">
              <Select options={DAYS.map((d) => ({ value: d, label: d }))} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="grace_minutes" label="Grace Minutes">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="ot_eligible" label="OT Eligible" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="ot_rate_multiplier" label="OT Rate Multiplier">
              <InputNumber style={{ width: '100%' }} step={0.1} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="manual_attendance_allowed" label="Manual Allowed" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="default_shift_id"
              label="Default Shift"
              tooltip="Which shift this worker belongs to (A / B / C / GEN). Drives variance task generation + Daily Attendance Report scheduling."
            >
              <Select
                allowClear
                placeholder="Pick from shift master"
                options={shifts.map((s) => ({
                  label: `${s.shift_code} — ${s.shift_name} (${s.start_time}–${s.end_time})`
                    + (s.is_active ? '' : ' (inactive)'),
                  value: s.id,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="shift_type"
              label="Shift Policy"
              tooltip="ROTATING = rotates A→B→C every week. FIXED_A = always A. GENERAL = office hours. Different from Default Shift (which is the actual shift today)."
            >
              <Select
                allowClear
                placeholder="ROTATING / FIXED / GENERAL"
                options={SHIFT_TYPE_POLICIES.map((p) => ({
                  label: p.replace(/_/g, ' '), value: p,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="shift_roster_group"
              label="Shift Roster Group"
              tooltip="Optional group label for shift rotation (e.g. 'Group-1' / 'Group-2'). Free text — used for roster planning."
            >
              <Input placeholder="e.g. Group-1" />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Space>
        <Button type="primary" htmlType="submit" disabled={!dirty} loading={save.isPending}>
          Save
        </Button>
        {dirty && <span style={{ color: '#faad14' }}>Unsaved changes</span>}
      </Space>
    </Form>
  )
}
